import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';

import { customerService } from '../../../services/api';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import ListFilterToolbar from '../../../components/dashboard/ListFilterToolbar';
import RowActionsMenu from '../../../components/dashboard/RowActionsMenu';
import SoftChip from '../../../components/dashboard/SoftChip';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';
import UndoSnackbar from '../../../components/dashboard/UndoSnackbar';
import useUndoDelete from '../../../components/dashboard/useUndoDelete';
import DataGridCard from '../../../components/dashboard/DataGridCard';
import { buildRoleColumns, scopeLabel } from './columns';

const STATUS_OPTIONS = ['active', 'inactive'];
const SCOPE_OPTIONS = ['global', 'business', 'team'];

export default function RolesList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();

  const [status, setStatus] = useState('');
  const [scope, setScope] = useState('');
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [selectionModel, setSelectionModel] = useState([]);
  const [showColumnBorder, setShowColumnBorder] = useState(false);
  const [showCellBorder, setShowCellBorder] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: () => customerService.getRoles().then((res) => res.data),
  });

  const roles = useMemo(() => data ?? [], [data]);

  const matchesFilters = useCallback(
    (role, { statusValue, scopeValue, searchValue }) => {
      if (statusValue !== '' && role.is_active !== (statusValue === 'active')) return false;
      if (scopeValue !== '' && role.scope !== scopeValue) return false;
      if (searchValue && !role.name.toLowerCase().includes(searchValue.toLowerCase())) return false;
      return true;
    },
    [],
  );

  const filteredRoles = useMemo(
    () => roles.filter((role) => matchesFilters(role, { statusValue: status, scopeValue: scope, searchValue: search })),
    [roles, status, scope, search, matchesFilters],
  );

  const applySelection = useCallback(
    (overrides) => {
      const visibleIds = roles
        .filter((role) =>
          matchesFilters(role, {
            statusValue: overrides.statusValue ?? status,
            scopeValue: overrides.scopeValue ?? scope,
            searchValue: overrides.searchValue ?? search,
          }),
        )
        .map((role) => role.uuid);
      setSelectionModel((prev) => prev.filter((id) => visibleIds.includes(id)));
    },
    [roles, status, scope, search, matchesFilters],
  );

  const updateStatus = (value) => {
    setStatus(value);
    applySelection({ statusValue: value });
  };

  const updateScope = (value) => {
    setScope(value);
    applySelection({ scopeValue: value });
  };

  const updateSearch = (value) => {
    setSearch(value);
    applySelection({ searchValue: value });
  };

  const clearFilters = () => {
    setStatus('');
    setScope('');
    setSearch('');
    applySelection({ statusValue: '', scopeValue: '', searchValue: '' });
  };

  const { pending, remove, undo, dismiss, seed } = useUndoDelete({
    onDelete: (uuid) => customerService.deleteRole(uuid).then(() => queryClient.invalidateQueries({ queryKey: ['roles'] })),
    onRestore: (uuid) => customerService.restoreRole(uuid).then(() => queryClient.invalidateQueries({ queryKey: ['roles'] })),
  });

  const [flashMessage, setFlashMessage] = useState('');

  useEffect(() => {
    const seedDelete = location.state?.seedDelete;
    if (seedDelete) {
      seed(seedDelete.ids, seedDelete.message);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    if (location.state?.flashMessage) {
      setFlashMessage(location.state.flashMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleDeleteOne = (role) => {
    remove(role.uuid, t('roles.deleteSuccess'));
    setSelectionModel((prev) => prev.filter((id) => id !== role.uuid));
  };

  const handleDeleteSelected = () => {
    const ids = [...selectionModel];
    remove(ids, t('roles.deleteSuccess'));
    setSelectionModel([]);
  };

  const columns = useMemo(
    () =>
      buildRoleColumns({
        t,
        onView: (role) => navigate(`/roles/${role.uuid}`),
        onEdit: (role) => navigate(`/roles/${role.uuid}/edit`),
        onDelete: handleDeleteOne,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, navigate],
  );

  const chips = [];
  if (status !== '') {
    chips.push({
      key: 'status',
      label: `${t('roles.filters.status')}: ${t(`roles.statuses.${status}`)}`,
      onDelete: () => updateStatus(''),
    });
  }
  if (scope !== '') {
    chips.push({
      key: 'scope',
      label: `${t('roles.filters.scope')}: ${scopeLabel(t, scope)}`,
      onDelete: () => updateScope(''),
    });
  }

  const header = (
    <PageHeader
      title={t('roles.listTitle')}
      breadcrumbs={[
        { label: t('roles.breadcrumb.dashboard') },
        { label: t('nav.groups.team') },
        { label: t('roles.breadcrumb.list') },
      ]}
      actions={
        <Button variant="contained" onClick={() => navigate('/roles/new')}>
          {t('roles.newRole')}
        </Button>
      }
    />
  );

  const selectedCount = selectionModel.length;

  // Plain props object, NOT a function/component: `slots.toolbar` must stay a
  // stable reference (the imported ListFilterToolbar) across renders. Handing
  // DataGrid a freshly-defined render function every render — even one that
  // just wraps the same component — makes React treat it as a different
  // component type each time, remounting the toolbar (and its focused search
  // input) after every keystroke.
  const toolbarProps = {
    filters: [
      {
        field: 'status',
        label: t('roles.filters.status'),
        value: status,
        onChange: updateStatus,
        options: STATUS_OPTIONS.map((value) => ({
          value,
          label: t(`roles.statuses.${value}`),
        })),
      },
      {
        field: 'scope',
        label: t('roles.filters.scope'),
        value: scope,
        onChange: updateScope,
        options: SCOPE_OPTIONS.map((value) => ({
          value,
          label: scopeLabel(t, value),
        })),
      },
    ],
    search: { label: t('roles.filters.search'), value: search, onChange: updateSearch },
    chips,
    resultsLabel: t('roles.filters.resultsFound', { count: filteredRoles.length }),
    clearLabel: t('roles.filters.clear'),
    onClearAll: clearFilters,
    gridSettings: {
      showColumnBorder,
      onShowColumnBorderChange: setShowColumnBorder,
      showCellBorder,
      onShowCellBorderChange: setShowCellBorder,
    },
    bulkAction: selectedCount > 0 && (
      <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDeleteSelected}>
        {t('roles.list.deleteSelected', { count: selectedCount })}
      </Button>
    ),
  };

  return (
    <BoundedPageLayout header={header}>
      <DataGridCard>
        {isMobile ? (
          <Box>
            <ListFilterToolbar {...toolbarProps} showGridControls={false} />
            {filteredRoles.length === 0 ? (
              <EmptyState t={t} />
            ) : (
              <List disablePadding>
                {filteredRoles
                  .slice(
                    paginationModel.page * paginationModel.pageSize,
                    (paginationModel.page + 1) * paginationModel.pageSize,
                  )
                  .map((role) => (
                    <ListItem
                      key={role.uuid}
                      divider
                      secondaryAction={
                        <RowActionsMenu
                          actions={[
                            { label: t('roles.actions.view'), icon: <VisibilityOutlinedIcon fontSize="small" />, onClick: () => navigate(`/roles/${role.uuid}`) },
                            { label: t('roles.actions.edit'), icon: <EditOutlinedIcon fontSize="small" />, onClick: () => navigate(`/roles/${role.uuid}/edit`) },
                            ...(role.is_owner
                              ? []
                              : [{ label: t('roles.actions.delete'), icon: <DeleteOutlineIcon fontSize="small" />, color: 'error', onClick: () => handleDeleteOne(role) }]),
                          ]}
                        />
                      }
                    >
                      <Checkbox
                        edge="start"
                        checked={selectionModel.includes(role.uuid)}
                        onChange={(event) =>
                          setSelectionModel((prev) =>
                            event.target.checked ? [...prev, role.uuid] : prev.filter((id) => id !== role.uuid),
                          )
                        }
                        sx={{ mr: 1 }}
                      />
                      <ListItemText
                        primary={role.name}
                        secondary={
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} useFlexGap flexWrap="wrap">
                            <Typography variant="caption" color="text.secondary">
                              {scopeLabel(t, role.scope)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('roles.list.permissionsCount', { count: role.permissions?.length ?? 0 })}
                            </Typography>
                            <SoftChip
                              size="small"
                              label={role.is_active ? t('roles.statuses.active') : t('roles.statuses.inactive')}
                              color={role.is_active ? 'success' : 'default'}
                            />
                          </Stack>
                        }
                      />
                    </ListItem>
                  ))}
              </List>
            )}
          </Box>
        ) : (
          <DataGrid
            // The "Perfil" column renders two lines (name + description);
            // a fixed density-based row height clips/overlaps that second
            // line in compact mode, so let each row size to its own content.
            getRowHeight={() => 'auto'}
            rows={filteredRoles}
            columns={columns}
            getRowId={(row) => row.uuid}
            loading={isLoading}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={(model) => setSelectionModel(model)}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            showColumnVerticalBorder={showColumnBorder}
            slots={{
              toolbar: ListFilterToolbar,
              noRowsOverlay: EmptyState,
              columnSelectorIcon: ViewColumnOutlinedIcon,
            }}
            slotProps={{
              toolbar: toolbarProps,
              noRowsOverlay: { t },
            }}
            localeText={{
              toolbarColumns: t('roles.filters.columns'),
              toolbarFilters: t('roles.filters.filters'),
            }}
            sx={{
              border: 0,
              ...(showCellBorder && {
                '& .MuiDataGrid-cell': { borderRight: '1px solid', borderColor: 'divider' },
              }),
            }}
          />
        )}
      </DataGridCard>

      <UndoSnackbar pending={pending} onUndo={undo} onDismiss={dismiss} undoLabel={t('roles.deleteUndo')} />

      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />
    </BoundedPageLayout>
  );
}

function EmptyState({ t }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 6, textAlign: 'center' }}>
      <Typography variant="body1">{t('roles.list.empty')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('roles.list.emptyHint')}
      </Typography>
    </Stack>
  );
}
